import './App.css'
import { ApolloProvider } from '@apollo/client'
import { MantineProvider } from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { Web3Auth } from '@web3auth/modal'
import { OpenloginAdapter } from '@web3auth/openlogin-adapter'
import { Web3AuthConnector } from '@web3auth/web3auth-wagmi-connector'
import React, { Suspense, useEffect, useMemo } from 'react'
import { withErrorBoundary } from 'react-error-boundary'
import { HelmetProvider } from 'react-helmet-async'
import { configureChains, createConfig, WagmiConfig } from 'wagmi'
import { InjectedConnector } from 'wagmi/connectors/injected'
import { publicProvider } from 'wagmi/providers/public'
import ErrorBoundaryFallback from '@/components/ErrorBoundaryFallback'
import UserContextProvider from '@/contexts/UserContext'
import { Web3AuthProvider } from '@/components/Web3Auth/web3auth'
import {
  l1IdSubnetWagmiConfig,
  l1NativeWagmiConfig,
  l1NativeWeb3AuthConfig,
} from '@/constants/config'
import {
  initModalParams,
  openLoginAdapterOptions,
  web3AuthOptions,
} from '@/components/Web3Auth/config'
import AppFrame from './components/AppFrame'
import QRCodeModal from './components/Modals/QRCodeModal'
import Seo from './components/Seo'
import { useCustomTheme } from './components/Theme/themes'
import useLocalStorage from './hooks/useLocalStorage'
import client from './plugins/apollo'
import { I18NextProvider } from './plugins/i18n'
import SnackbarProvider from './plugins/SnackbarProvider'
import { StoredTheme } from './utils/utils'

// TODO: move all these types and constants to a separate file; bigger task because the code
//       related to wagmi and web3auth are split across the codebase, so refactoring
//       will be required

// Native Lamina1 Chain defined as default
export const chain = l1NativeWeb3AuthConfig
const openloginAdapter = new OpenloginAdapter(openLoginAdapterOptions)
export const web3AuthInstance = new Web3Auth({
  chainConfig: chain,
  ...web3AuthOptions,
})
web3AuthInstance.configureAdapter(openloginAdapter)

const { chains, publicClient, webSocketPublicClient } = configureChains(
  [l1NativeWagmiConfig, l1IdSubnetWagmiConfig],
  [publicProvider()]
)

export const web3AuthConnector = new Web3AuthConnector({
  chains,
  options: {
    web3AuthInstance,
    ...initModalParams,
  },
})

export const injectedConnector = new InjectedConnector({
  chains,
})

const config = createConfig({
  autoConnect: true,
  publicClient,
  webSocketPublicClient,
  connectors: [web3AuthConnector, injectedConnector],
})

function App() {
  const contextModals = useMemo(
    () => ({
      qrCode: QRCodeModal,
    }),
    [QRCodeModal]
  )

  const [userTheme, setUserTheme] = useLocalStorage<StoredTheme>(`userTheme`, {
    isLightMode: window?.matchMedia?.('(prefers-color-scheme: light)')?.matches ?? false,
  })

  const { activeTheme, mantineTheme } = useCustomTheme(userTheme.isLightMode)

  useEffect(() => {
    // Update CSS variables to match the active theme
    document.documentElement.style.setProperty('--background-color', activeTheme.background)
    document.documentElement.style.setProperty('--background2-color', activeTheme.background2)
    document.documentElement.style.setProperty('--background3-color', activeTheme.background3)
    document.documentElement.style.setProperty('--title-color', activeTheme.title)
    document.documentElement.style.setProperty('--textAndIcon-color', activeTheme.textAndIcon)
    document.documentElement.style.setProperty('--profileOutline-color', activeTheme.profileOutline)
    document.documentElement.style.setProperty('--text2-color', activeTheme.paragraph)
    document.documentElement.style.setProperty('--l1Primary-color', activeTheme.l1Primary)
    document.documentElement.style.setProperty('--l1Secondary-color', activeTheme.l1Secondary)
  }, [activeTheme])

  return (
    <React.StrictMode>
      <Suspense fallback={<div>Still loading...</div>}>
        <I18NextProvider>
          <ApolloProvider client={client}>
            <div className="App">
              <MantineProvider theme={mantineTheme}>
                <HelmetProvider>
                  <Seo
                    title="Lamina1"
                    description="Welcome to the LAMINA1 Hub, your gateway to the open metaverse. This is a testing and development ground for the LAMINA1 Betanet. Come build with us."
                    type="website"
                    creator="Lamina1"
                    url="https://hub.lamina1.com"
                    image="./logo_og.jpeg"
                  />
                  <SnackbarProvider>
                    <ModalsProvider modals={contextModals}>
                      <WagmiConfig config={config}>
                        <UserContextProvider>
                          <Web3AuthProvider>
                            <AppFrame />
                          </Web3AuthProvider>
                        </UserContextProvider>
                      </WagmiConfig>
                    </ModalsProvider>
                  </SnackbarProvider>
                </HelmetProvider>
              </MantineProvider>
            </div>
          </ApolloProvider>
        </I18NextProvider>
      </Suspense>
    </React.StrictMode>
  )
}

export default withErrorBoundary(App, {
  FallbackComponent: ErrorBoundaryFallback,
})
